import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { jobsApi } from "@/api/client";
import {
  deriveJobControlState,
  type JobControlState,
} from "@/pages/pipelineControls";
import { normalizeState } from "@/pages/pipelineHelpers";
import { isWindowed } from "@/pages/pipelineOperations";
import type { CreateJobRequest, JobMetadata, Workspace } from "@/types/api";
import { editsLockedFor } from "./pipelineStaging";
import type { OpenPipeline } from "./StudioPipelineProvider";

// The job side of the pipeline the Studio has open: the job a workspace
// points at, and the start/stop lifecycle (with the convergence poll the
// badge rides on). The provider owns the open-state bookkeeping; nothing
// here touches the editor.

/** The message a failed lifecycle action reports — an Error or anything
 *  else a rejected call threw. */
export const failureText = (e: unknown) =>
  e instanceof Error ? e.message : String(e);

/** Whether a job runs exactly the pipeline `request` deployed: every stage
 *  and its settings, the transforms, schemas, batching, checkpointing,
 *  mode, which operations it takes and routes, and what it does with a
 *  record it gives up on, compared whole. Anything the engine filled in or
 *  changed reads as different, which
 *  only means a job found under the name is not adopted. */
export function samePipeline(
  job: JobMetadata,
  request: Pick<
    CreateJobRequest,
    | "source"
    | "middlewares"
    | "sinks"
    | "schemas"
    | "checkpoint"
    | "batch"
    | "operation_types"
    | "routes"
    | "on_record_failure"
  >,
): boolean {
  const pipeline = job.pipeline;
  if (!pipeline) return false;
  const shape = (p: typeof request & { batch?: unknown; mode?: unknown }) => ({
    source: unsetRouteDropped(p.source),
    middlewares: (p.middlewares ?? []).map(unsetRouteDropped),
    sinks: p.sinks.map(unsetRouteDropped),
    schemas: p.schemas ?? [],
    checkpoint: p.checkpoint ?? null,
    batch: p.batch ?? null,
    // The engine's default when a pipeline names none.
    mode: p.mode ?? "move",
    // Absent, null and (for routes) empty all mean "none" to the engine,
    // which leaves both out of what it stores.
    operation_types: p.operation_types ?? null,
    routes: p.routes ?? [],
    on_record_failure: recordFailure(p),
  });
  return canonical(shape(pipeline)) === canonical(shape(request));
}

/** The pipeline's record failure action, with the engine's default "halt"
 *  filled in when it is unset (absent or null). A windowed pipeline has no
 *  default: it retries its own failures and refuses any action, so unset
 *  compares as null. */
function recordFailure(
  p: Pick<CreateJobRequest, "on_record_failure" | "batch">,
): unknown {
  // Any value written, even one the engine refuses, is compared as written.
  if (p.on_record_failure != null) return p.on_record_failure;
  return isWindowed(p as Record<string, unknown>) ? null : "halt";
}

/** A stage as the engine stores it: a `route_id` of null means none, and
 *  the engine leaves it out. */
function unsetRouteDropped<T extends { route_id?: string | null }>(
  stage: T,
): T {
  if (stage == null || stage.route_id !== null) return stage;
  const { route_id: _unset, ...rest } = stage;
  return rest as T;
}

/** JSON with object keys sorted, so equal values compare equal as text. */
function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, v) =>
    v && typeof v === "object" && !Array.isArray(v)
      ? Object.fromEntries(
          Object.entries(v as Record<string, unknown>).sort(([a], [b]) =>
            a < b ? -1 : a > b ? 1 : 0,
          ),
        )
      : v,
  );
}

/** Which of the two lifecycle buttons is waiting on the backend. */
type JobPending = { starting: boolean; stopping: boolean };

type SetOpenPipeline = Dispatch<SetStateAction<OpenPipeline | null>>;

/** The live job a workspace's deployed_job points at, or null when it names
 *  nothing (any more) — a workspace whose job was deleted opens as a draft. */
export async function deployedJobFor(
  ws: Workspace,
): Promise<JobMetadata | null> {
  if (!ws.deployed_job) return null;
  try {
    const jobs = await jobsApi.list();
    return jobs.find((j) => j.name === ws.deployed_job) ?? null;
  } catch {
    // An unreachable jobs list keeps the workspace open as a draft.
    return null;
  }
}

/** Stop the job a workspace already deployed before it is deployed again —
 *  /studio's stopExistingJob recipe (usePipelineState.ts): a redeploy must
 *  not race the pipeline it replaces. Failure is expected and ignored (the
 *  job may be stopped already, or gone). */
export async function stopExistingJob(jobName: string | null | undefined) {
  if (!jobName) return;
  try {
    await jobsApi.stop(jobName);
  } catch {
    // Already stopped, already deleted — the deploy carries on either way.
  }
}

// Whether opening this job lands in the view-only studio (a running or
// converging job) — decided from the job itself, before any lock state
// exists for it (the same rule editsLocked applies once it is open).
export function viewOnlyFor(job: JobMetadata | null): boolean {
  if (!job) return false;
  const { displayState } = deriveJobControlState(
    normalizeState(job.state),
    normalizeState(job.desired_state),
  );
  return editsLockedFor("deployed", displayState);
}

/** Resolves once `jobName` is neither running nor converging (or is gone):
 *  the stop endpoint returns while the worker is still winding down, and
 *  the backend refuses to delete a job whose state still reads running. */
async function waitForJobStopped(jobName: string): Promise<void> {
  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    if (!viewOnlyFor((await fetchJob(jobName)) ?? null)) return;
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
  throw new Error(`"${jobName}" is still stopping — try again in a moment`);
}

/** Saving an edited stopped/failed pipeline as a draft takes its job off
 *  the Pipelines list: the draft is the only version from then on, and
 *  Deploy recreates the job. A job that started running in the meantime
 *  is left alone unless the caller said to stop it first (the header asks). */
export async function retireJob(
  jobName: string,
  stopFirst: boolean,
): Promise<"retired" | "running"> {
  const job = await fetchJob(jobName);
  if (!job) return "retired";
  if (viewOnlyFor(job)) {
    if (!stopFirst) return "running";
    await jobsApi.stop(jobName);
    await waitForJobStopped(jobName);
  }
  await jobsApi.delete(jobName);
  return "retired";
}

/** The job name an open pipeline runs under, or null when what is open is
 *  no job (nothing open, or a draft). */
function deployedJobName(open: OpenPipeline | null): string | null {
  return open?.kind === "deployed" ? open.job.name : null;
}

/** Badge + button convergence for the open pipeline — the deployed state
 *  has one, everything else has none. */
function jobControlFor(
  open: OpenPipeline | null,
  pending: JobPending,
): JobControlState | null {
  if (open?.kind !== "deployed") return null;
  return deriveJobControlState(
    normalizeState(open.job.state),
    normalizeState(open.job.desired_state),
    pending,
  );
}

// ── Start / stop ──────────────────────────────────────────

// The backend flips desired_state immediately but applies the change
// asynchronously (HA), so a start/stop is followed by a short poll that
// carries the stored job forward until state and desired_state agree —
// deriveJobControlState reads that divergence as the transition and keeps
// the badge honest ("Starting"/"Stopping") with both buttons disabled. The
// pending flags cover the gap before the first poll returns.
const POLL_MS = 2000;
const POLL_ATTEMPTS = 15;

// One poll at a time, and it belongs to one job: `jobName` names it so
// switching pipelines can abandon it without touching the poll a fresh
// deploy just started for the pipeline being entered.
interface PollState {
  timer: ReturnType<typeof setInterval> | null;
  jobName: string | null;
  attempts: number;
  inFlight: boolean;
}

interface PollOptions {
  jobName: string;
  setOpenPipeline: SetOpenPipeline;
  endPoll: () => void;
}

/** Drop the timer and the job it belonged to; the pending flags are the
 *  caller's (endPoll clears those too). */
function stopPoll(poll: PollState) {
  if (poll.timer !== null) clearInterval(poll.timer);
  poll.timer = null;
  poll.jobName = null;
}

/** The job as the backend has it now, or undefined when it is gone — a
 *  failed look-up reads as gone too, and ends the poll with the stored job
 *  on its last state. */
async function fetchJob(jobName: string): Promise<JobMetadata | undefined> {
  try {
    return (await jobsApi.list()).find((j) => j.name === jobName);
  } catch {
    return undefined;
  }
}

/** Nothing left to wait for: the backend agrees with itself, or the job
 *  isn't there any more. */
function pollSettled(job: JobMetadata | undefined): boolean {
  if (!job) return true;
  return normalizeState(job.state) === normalizeState(job.desired_state);
}

/** The polled job folded into the open state — only while the pipeline it
 *  belongs to is still the one open. */
function withPolledJob(
  prev: OpenPipeline | null,
  job: JobMetadata,
): OpenPipeline | null {
  if (prev?.kind !== "deployed") return prev;
  if (prev.job.name !== job.name) return prev;
  return { ...prev, job };
}

/** Whether a tick's answer still speaks for the poll it started under —
 *  not once the poll ended, or another job's poll replaced it, while the
 *  look-up was out. */
function pollCurrent(poll: PollState, jobName: string): boolean {
  return poll.timer !== null && poll.jobName === jobName;
}

/** One poll tick: carry the stored job forward, and end the poll once the
 *  backend has settled (or it has waited long enough). One look-up at a
 *  time: a slow one makes the next tick wait rather than stack requests
 *  whose answers could land out of order. */
async function pollTick(poll: PollState, options: PollOptions) {
  if (poll.inFlight) return;
  poll.inFlight = true;
  poll.attempts += 1;
  const fresh = await fetchJob(options.jobName);
  poll.inFlight = false;
  if (!pollCurrent(poll, options.jobName)) return;
  if (fresh) options.setOpenPipeline((prev) => withPolledJob(prev, fresh));
  if (pollSettled(fresh) || poll.attempts >= POLL_ATTEMPTS) options.endPoll();
}

/** Start polling for `jobName`, dropping any timer still running. Only the
 *  timer: the pending flag the caller just set stands until the backend
 *  converges. */
function startPoll(poll: PollState, options: PollOptions) {
  stopPoll(poll);
  poll.jobName = options.jobName;
  poll.attempts = 0;
  poll.timer = setInterval(() => {
    void pollTick(poll, options);
  }, POLL_MS);
}

/** Opening another pipeline abandons the poll of the one left behind. */
function abandonForeignPoll(
  poll: PollState,
  openJobName: string | null,
  endPoll: () => void,
) {
  if (poll.jobName === null) return;
  if (poll.jobName === openJobName) return;
  endPoll();
}

export function useJobControls(
  openPipeline: OpenPipeline | null,
  setOpenPipeline: SetOpenPipeline,
  reportError: (message: string) => void,
) {
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const pollRef = useRef<PollState>({
    timer: null,
    jobName: null,
    attempts: 0,
    inFlight: false,
  });

  // The poll is over: no timer, and the buttons stop waiting on it.
  const endPoll = useCallback(() => {
    stopPoll(pollRef.current);
    setStarting(false);
    setStopping(false);
  }, []);

  // Unmount abandons the poll. endPoll is stable, so this cleanup runs on
  // unmount only — a state change must NOT kill a running poll (deploy
  // starts one while entering the deployed state).
  useEffect(() => endPoll, [endPoll]);

  const openJobName = deployedJobName(openPipeline);
  useEffect(() => {
    abandonForeignPoll(pollRef.current, openJobName, endPoll);
  }, [openJobName, endPoll]);

  const pollUntilSettled = useCallback(
    (jobName: string) => {
      startPoll(pollRef.current, { jobName, setOpenPipeline, endPoll });
    },
    [setOpenPipeline, endPoll],
  );

  const run = useCallback(
    async (
      what: "Start" | "Stop",
      setPending: (pending: boolean) => void,
      call: (jobName: string) => Promise<unknown>,
    ) => {
      if (openJobName === null) return;
      setPending(true);
      try {
        await call(openJobName);
        pollUntilSettled(openJobName);
      } catch (e) {
        endPoll();
        reportError(`${what} failed: ${failureText(e)}`);
      }
    },
    [openJobName, pollUntilSettled, endPoll, reportError],
  );

  const startPipeline = useCallback(
    () => run("Start", setStarting, (name) => jobsApi.restart(name)),
    [run],
  );
  const stopPipeline = useCallback(
    () => run("Stop", setStopping, (name) => jobsApi.stop(name)),
    [run],
  );

  const jobControl = jobControlFor(openPipeline, { starting, stopping });

  // pollUntilSettled is shared with deploy: a job just created is stopped
  // (or starting) against a desired Running, which reads as converging —
  // without the poll the badge would sit on "Starting" with both buttons
  // disabled until the pipeline is reopened.
  return { jobControl, startPipeline, stopPipeline, pollUntilSettled };
}
